import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  Check,
  ArrowRight,
  Calendar,
  Users,
  User as UserIcon
} from 'lucide-react';

const TodaysTasks = () => {
  const API = getBackendUrl() || '';
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch today's tasks
  const fetchTodaysTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
      
      const response = await axios.get(
        `${API}/api/tasks?start_date=${today}&end_date=${today}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTasks(response.data.slice(0, 5)); // Show max 5 tasks
    } catch (error) {
      console.error('Error fetching today\'s tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodaysTasks();
  }, []);

  // Toggle task status
  const handleToggleStatus = async (task) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await axios.patch(`${API}/api/tasks/${task.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Task marked as ${newStatus}`);
      fetchTodaysTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  // Priority badge colors
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">Today's Tasks</h2>
        <Link
          to="/tasks"
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          View All
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="text-center py-4 text-gray-500">Loading...</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No tasks for today</p>
          <Link
            to="/tasks"
            className="inline-flex items-center gap-1 mt-3 text-blue-600 hover:text-blue-800 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add a task
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map(task => (
            <div
              key={task.id}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {/* Status Checkbox */}
              <button
                onClick={() => handleToggleStatus(task)}
                className="mt-0.5"
              >
                {task.status === 'completed' ? (
                  <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-300 rounded hover:border-blue-500" />
                )}
              </button>

              {/* Task Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-medium ${
                    task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'
                  }`}>
                    {task.title}
                  </p>
                  
                  {/* Task Type Badge - More Visible */}
                  {task.task_type === 'shared' ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded font-medium">
                      <Users className="w-3 h-3" />
                      SHARED
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded font-medium">
                      <UserIcon className="w-3 h-3" />
                      PERSONAL
                    </span>
                  )}
                </div>
                
                {/* Meta Info */}
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded capitalize font-medium ${getPriorityColor(task.priority)}`}>
                    ⚡ {task.priority}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Task Button */}
      <Link
        to="/tasks"
        className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
      >
        <Plus className="w-4 h-4" />
        Add Task
      </Link>
    </div>
  );
};

export default TodaysTasks;
